// Upstash Redis caching utility for sensor data
// CRA/Netlify use process.env.REACT_APP_*; Vite uses import.meta.env
function getRedisEnv(key) {
  const reactKey = `REACT_APP_${key}`;
  if (typeof process !== 'undefined' && process.env && process.env[reactKey]) {
    return process.env[reactKey];
  }
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
    return import.meta.env[key];
  }
  return undefined;
}

export class UpstashSensorDataCache {
  constructor() {
    this.redisUrl = getRedisEnv('UPSTASH_REDIS_REST_URL');
    this.redisToken = getRedisEnv('UPSTASH_REDIS_REST_TOKEN');

    if (!this.redisUrl || !this.redisToken) {
      console.warn('⚠️ Redis configuration missing, caching disabled. Set REACT_APP_UPSTASH_REDIS_REST_URL and REACT_APP_UPSTASH_REDIS_REST_TOKEN in Netlify environment variables.');
      this.redisUrl = '';
      this.redisToken = '';
      return;
    }

    console.log('🔗 Redis configured (caching enabled)');
  }

  /**
   * Generate cache key for sensor readings
   */
  getCacheKey(sensorId, period, startTime, endTime) {
    // Normalize dates to create consistent cache keys
    const start = new Date(startTime);
    
    // Create period-specific normalized keys for better cache hits
    let normalizedKey;
    
    switch (period) {
      case 'HOUR':
        // Round to nearest hour for consistent caching
        const hourStart = new Date(start);
        hourStart.setMinutes(0, 0, 0);
        normalizedKey = `${hourStart.getFullYear()}-${hourStart.getMonth()}-${hourStart.getDate()}-${hourStart.getHours()}`;
        break;
        
      case 'DAY':
        // Use just the date for daily cache
        normalizedKey = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
        break;
        
      case 'WEEK':
        // Use week start date
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() - start.getDay()); // Start of week
        normalizedKey = `${weekStart.getFullYear()}-W${Math.ceil(weekStart.getDate()/7)}`;
        break;
        
      case 'MONTH':
        // Use year-month
        normalizedKey = `${start.getFullYear()}-${start.getMonth()}`;
        break;
        
      case 'YEAR':
        // Use just the year
        normalizedKey = `${start.getFullYear()}`;
        break;
        
      default:
        // Fallback to original method for custom periods
        const timeHash = btoa(`${startTime}-${endTime}`).slice(0, 8);
        normalizedKey = timeHash;
    }
    
    return `sensor:${sensorId}:${period}:${normalizedKey}`;
  }

  /**
   * Get TTL (Time To Live) in seconds based on period
   */
  getTTL(period) {
    switch (period) {
      case 'HOUR': return 180; // 3 minutes - more frequent updates for recent data
      case 'DAY': return 600; // 10 minutes - reduced from 30 minutes
      case 'WEEK': return 1800; // 30 minutes - reduced from 1 hour
      case 'MONTH': return 3600; // 1 hour - reduced from 2 hours
      case 'YEAR': return 7200; // 2 hours - reduced from 4 hours
      case 'LATEST': return 60; // 1 minute for latest readings - much more frequent
      default: return 300; // 5 minutes default - much shorter
    }
  }

  /**
   * Make HTTP request to Upstash Redis REST API
   */
  async redisRequest(command) {
    // If Redis is not configured, return null
    if (!this.redisUrl || !this.redisToken) {
      return null;
    }

    try {
      const response = await fetch(this.redisUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.redisToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(command),
      });

      if (!response.ok) {
        throw new Error(`Redis request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.result;
    } catch (error) {
      console.warn('⚠️ Redis request failed:', error);
      throw error;
    }
  }

  /**
   * Cache sensor readings using Upstash Redis
   */
  async cacheSensorReadings(sensorId, period, startTime, endTime, readings) {
    try {
      const cacheKey = this.getCacheKey(sensorId, period, startTime, endTime);
      const ttl = this.getTTL(period);
      
      const cacheData = {
        readings,
        cached_at: new Date().toISOString(),
        period,
        count: readings.length
      };

      // Use SETEX command to set with expiration
      await this.redisRequest(['SETEX', cacheKey, ttl.toString(), JSON.stringify(cacheData)]);

      console.log(`📦 Cached ${readings.length} readings for ${period} (TTL: ${ttl}s, Key: ${cacheKey})`);
    } catch (error) {
      console.warn('⚠️ Redis cache write failed:', error);
      // Don't throw - caching is optional
    }
  }

  /**
   * Get cached sensor readings
   */
  async getCachedSensorReadings(sensorId, period, startTime, endTime) {
    try {
      const cacheKey = this.getCacheKey(sensorId, period, startTime, endTime);
      
      // Use GET command
      const cachedValue = await this.redisRequest(['GET', cacheKey]);

      if (cachedValue) {
        const cacheData = JSON.parse(cachedValue);
        const cacheAge = Date.now() - new Date(cacheData.cached_at).getTime();
        
        // For LATEST and HOUR periods, check if data is too stale
        if ((period === 'LATEST' || period === 'HOUR') && cacheAge > 300000) { // 5 minutes
          console.log(`⚠️ Cache data too stale (${Math.round(cacheAge/1000)}s), skipping cache`);
          // Delete stale cache entry
          await this.redisRequest(['DEL', cacheKey]);
          return null;
        }
        
        console.log(`🎯 Cache HIT for ${period}: ${cacheData.count} readings (age: ${Math.round(cacheAge/1000)}s)`);
        return cacheData.readings;
      }

      console.log(`❌ Cache MISS for ${period} (Key: ${cacheKey})`);
      return null;
    } catch (error) {
      console.warn('⚠️ Redis cache read failed:', error);
      return null; // Fallback to database
    }
  }

  /**
   * Cache latest reading for a specific sensor with optimized key
   */
  async cacheLatestReading(sensorId, reading) {
    try {
      const cacheKey = `latest:${sensorId}`;
      const ttl = 300; // 5 minutes for latest readings
      
      const cacheData = {
        reading,
        cached_at: new Date().toISOString(),
        sensor_id: sensorId
      };

      await this.redisRequest(['SETEX', cacheKey, ttl.toString(), JSON.stringify(cacheData)]);
      console.log(`📦 Cached latest reading for sensor ${sensorId} (TTL: ${ttl}s)`);
    } catch (error) {
      console.warn('⚠️ Failed to cache latest reading:', error);
    }
  }

  /**
   * Get cached latest reading for a specific sensor
   */
  async getCachedLatestReading(sensorId) {
    try {
      const cacheKey = `latest:${sensorId}`;
      const cachedValue = await this.redisRequest(['GET', cacheKey]);

      if (cachedValue) {
        const cacheData = JSON.parse(cachedValue);
        const cacheAge = Date.now() - new Date(cacheData.cached_at).getTime();
        
        console.log(`🎯 Latest reading cache HIT for sensor ${sensorId} (age: ${Math.round(cacheAge/1000)}s)`);
        return cacheData.reading;
      }

      console.log(`❌ Latest reading cache MISS for sensor ${sensorId}`);
      return null;
    } catch (error) {
      console.warn('⚠️ Latest reading cache read failed:', error);
      return null;
    }
  }

  /**
   * Batch get multiple latest readings
   */
  async batchGetLatestReadings(sensorIds) {
    try {
      const pipeline = sensorIds.map(sensorId => ['GET', `latest:${sensorId}`]);
      const results = await Promise.all(pipeline.map(cmd => this.redisRequest(cmd)));
      
      const readings = {};
      let hitCount = 0;
      
      results.forEach((result, index) => {
        const sensorId = sensorIds[index];
        if (result) {
          try {
            const cacheData = JSON.parse(result);
            readings[sensorId] = cacheData.reading;
            hitCount++;
          } catch (parseError) {
            console.warn(`⚠️ Failed to parse cached reading for sensor ${sensorId}:`, parseError);
          }
        }
      });
      
      console.log(`🎯 Batch latest readings: ${hitCount}/${sensorIds.length} cache hits`);
      return readings;
    } catch (error) {
      console.warn('⚠️ Batch get latest readings failed:', error);
      return {};
    }
  }

  /**
   * Batch cache multiple latest readings
   */
  async batchCacheLatestReadings(readings) {
    try {
      const ttl = 300; // 5 minutes
      const pipeline = [];

      readings.forEach(({ sensorId, reading }) => {
        const cacheKey = `latest:${sensorId}`;
        const cacheData = {
          reading,
          cached_at: new Date().toISOString(),
          sensor_id: sensorId
        };
        
        pipeline.push(this.redisRequest(['SETEX', cacheKey, ttl.toString(), JSON.stringify(cacheData)]));
      });

      // Execute all cache operations in parallel
      await Promise.all(pipeline);
      console.log(`📦 Batch cached ${readings.length} latest readings`);
    } catch (error) {
      console.warn('⚠️ Batch cache latest readings failed:', error);
    }
  }

  /**
   * Clear cache for a specific sensor (useful when new data is added)
   */
  async clearSensorCache(sensorId) {
    try {
      // Get all keys matching the pattern for both latest and historical data
      const patterns = [`sensor:${sensorId}:*`, `latest:${sensorId}`];
      const allKeys = [];
      
      for (const pattern of patterns) {
        try {
          const keys = await this.redisRequest(['KEYS', pattern]);
          if (keys && Array.isArray(keys)) {
            allKeys.push(...keys);
          } else if (keys) {
            allKeys.push(keys);
          }
        } catch (keyError) {
          console.warn(`⚠️ Failed to get keys for pattern ${pattern}:`, keyError);
        }
      }
      
      if (allKeys.length > 0) {
        // Delete all matching keys
        await this.redisRequest(['DEL', ...allKeys]);
        console.log(`🗑️ Cleared ${allKeys.length} cache entries for sensor ${sensorId}`);
      } else {
        console.log(`🗑️ No cache entries found for sensor ${sensorId}`);
      }
    } catch (error) {
      console.warn('⚠️ Redis cache clear failed:', error);
    }
  }

  /**
   * Test Redis connection
   */
  async ping() {
    try {
      const result = await this.redisRequest(['PING']);
      return result === 'PONG';
    } catch (error) {
      console.warn('⚠️ Redis ping failed:', error);
      return false;
    }
  }
}

// Export singleton instance
let cacheInstance = null;

export const getUpstashSensorDataCache = () => {
  if (!cacheInstance) {
    cacheInstance = new UpstashSensorDataCache();
  }
  return cacheInstance;
};
